import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert, Button, Card, Col, DatePicker, Empty, Input, Row, Segmented, Select,
  Space, Statistic, Table, Tag, Typography
} from 'antd';
import {
  ReloadOutlined, PrinterOutlined, DownloadOutlined, HddOutlined
} from '@ant-design/icons';
import { ErpMasterShell } from '../../../../components/common/erp-grid';
import { accountReportsApi } from './accountReportsApi';
import { formatINR, shortDate } from './accountReportsConstants';
import {
  FA_STATUS_COLOR, FA_STATUS_LABEL, friendlyReportError, formatRupee
} from './reportGroupingHelpers';
import '../accounts.css';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

// ─────────────────────────────────────────────────────────────────
// Glass-i — Fixed Asset Schedule page (Stage 6B.9G).
//
// Read-only schedule of fixed_assets register: cost / accumulated
// depreciation / WDV per asset, grouped by category. Backend computes
// totals + grouping; this page is presentation only.
// ─────────────────────────────────────────────────────────────────

const STATUS_OPTIONS = [
  { value: '',         label: 'All statuses' },
  { value: 'active',   label: 'Active' },
  { value: 'sold',     label: 'Sold' },
  { value: 'scrapped', label: 'Scrapped' },
  { value: 'retired',  label: 'Retired' }
];

function csvSafe(v) {
  if (v === null || v === undefined) return '';
  const s = String(v);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

function downloadCsv(filename, rows) {
  const cols = [
    'assetCode', 'assetName', 'categoryName', 'purchaseDate',
    'capitalizationDate', 'originalCost', 'additionalCost', 'totalCost',
    'accumulatedDepreciation', 'writtenDownValue', 'status',
    'disposalDate', 'saleValue', 'gainLossOnDisposal'
  ];
  const lines = [cols.join(',')];
  for (const r of rows) {
    lines.push(cols.map((c) => csvSafe(r[c])).join(','));
  }
  const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement('a');
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click(); document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export default function FixedAssetSchedulePage() {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [asOfDate, setAsOfDate]   = useState(null);
  const [range, setRange]         = useState(null);
  const [status, setStatus]       = useState('');
  const [q, setQ]                 = useState('');
  const [view, setView]           = useState('flat');

  const fetchReport = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const params = {};
      if (asOfDate) params.asOfDate = asOfDate.format('YYYY-MM-DD');
      if (range && range[0] && range[1]) {
        params.fromDate = range[0].format('YYYY-MM-DD');
        params.toDate   = range[1].format('YYYY-MM-DD');
      }
      if (status) params.status = status;
      if (q)      params.q      = q;
      const out = await accountReportsApi.fixedAssetSchedule(params);
      setData(out);
    } catch (e) {
      setLoadError(e);
    } finally { setLoading(false); }
  }, [asOfDate, range, status, q]);

  useEffect(() => { fetchReport(); }, [fetchReport]);

  const rows    = data?.rows    || [];
  const groups  = data?.groups  || [];
  const summary = data?.summary || {
    totalOriginalCost: 0, totalAdditionalCost: 0, totalCost: 0,
    totalAccumulatedDepreciation: 0, totalWdv: 0,
    activeCount: 0, disposedCount: 0, totalCount: 0
  };
  const meta = data?.meta || {};
  const groupedAvailable = !!meta.groupedViewAvailable && groups.length > 0;

  const columns = useMemo(() => ([
    { title: 'Code', dataIndex: 'assetCode', key: 'assetCode', width: 140,
      render: (v) => <code style={{ fontSize: 12 }}>{v}</code> },
    { title: 'Asset Name', dataIndex: 'assetName', key: 'assetName' },
    { title: 'Category', dataIndex: 'categoryName', key: 'categoryName', width: 160,
      render: (v) => v || <Text type="secondary">—</Text> },
    { title: 'Purchase', dataIndex: 'purchaseDate', key: 'purchaseDate', width: 110,
      render: (v) => shortDate(v) },
    { title: 'Cap. Date', dataIndex: 'capitalizationDate', key: 'capitalizationDate', width: 110,
      render: (v) => shortDate(v) },
    { title: 'Total Cost (₹)', dataIndex: 'totalCost', key: 'totalCost', width: 130, align: 'right',
      render: (v) => formatINR(v) },
    { title: 'Accum. Depr. (₹)', dataIndex: 'accumulatedDepreciation', key: 'accumulatedDepreciation', width: 130, align: 'right',
      render: (v) => formatINR(v) },
    { title: 'WDV (₹)', dataIndex: 'writtenDownValue', key: 'writtenDownValue', width: 130, align: 'right',
      render: (v) => <Text strong>{formatINR(v)}</Text> },
    { title: 'Status', dataIndex: 'status', key: 'status', width: 110,
      render: (v) => v
        ? <Tag color={FA_STATUS_COLOR[v] || 'default'}>{FA_STATUS_LABEL[v] || v}</Tag>
        : <Text type="secondary">—</Text> }
  ]), []);

  return (
    <ErpMasterShell>
      <div className="erp-accounts-page">
        <div style={{ display: 'flex', alignItems: 'center',
                      justifyContent: 'space-between', marginBottom: 8 }}>
          <Title level={4} style={{ margin: 0 }}>
            <HddOutlined style={{ marginRight: 8, color: '#0369a1' }} />
            Fixed Asset Schedule
          </Title>
          <Space>
            <Button size="small" icon={<ReloadOutlined />}
              onClick={fetchReport} loading={loading}>Refresh</Button>
            <Button size="small" icon={<PrinterOutlined />}
              onClick={() => window.print()}>Print</Button>
            <Button size="small" icon={<DownloadOutlined />}
              disabled={!rows.length}
              onClick={() => downloadCsv('fixed-asset-schedule.csv', rows)}>
              Export CSV
            </Button>
          </Space>
        </div>
        <div style={{ fontSize: 12, color: '#64748b', marginBottom: 10 }}>
          Asset cost, accumulated depreciation and written-down value across the register.
        </div>

        {/* Summary cards */}
        <Row gutter={[10, 10]} style={{ marginBottom: 10 }}>
          <Col xs={12} md={5}>
            <Card size="small">
              <Statistic title="Total Cost (₹)" value={summary.totalCost} precision={2} />
            </Card>
          </Col>
          <Col xs={12} md={5}>
            <Card size="small">
              <Statistic title="Accumulated Depr. (₹)" value={summary.totalAccumulatedDepreciation}
                precision={2} valueStyle={{ color: '#b65b00' }} />
            </Card>
          </Col>
          <Col xs={12} md={5}>
            <Card size="small">
              <Statistic title="WDV (₹)" value={summary.totalWdv} precision={2}
                valueStyle={{ color: '#0369a1' }} />
            </Card>
          </Col>
          <Col xs={12} md={4}>
            <Card size="small">
              <Statistic title="Active" value={summary.activeCount} />
            </Card>
          </Col>
          <Col xs={12} md={5}>
            <Card size="small">
              <Statistic title="Disposed / Other" value={summary.disposedCount} />
              <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>
                Total {summary.totalCount} asset(s)
              </div>
            </Card>
          </Col>
        </Row>

        {/* Filters */}
        <Card size="small" className="erp-accounts-filter" style={{ marginBottom: 10 }}>
          <Space wrap>
            <Space>
              <Text>View:</Text>
              <Segmented size="small" value={view} onChange={setView}
                options={[
                  { label: 'Flat',    value: 'flat' },
                  { label: 'Grouped', value: 'grouped', disabled: !groupedAvailable }
                ]} />
            </Space>
            <Space>
              <Text>As of date:</Text>
              <DatePicker size="small" value={asOfDate} onChange={setAsOfDate}
                allowClear format="YYYY-MM-DD" />
            </Space>
            <Space>
              <Text>Capitalization range:</Text>
              <RangePicker size="small" value={range} onChange={setRange}
                allowClear format="YYYY-MM-DD" />
            </Space>
            <Space>
              <Text>Status:</Text>
              <Select size="small" value={status} onChange={setStatus}
                style={{ width: 140 }} options={STATUS_OPTIONS} />
            </Space>
            <Space>
              <Text>Search:</Text>
              <Input.Search size="small" allowClear value={q}
                onChange={(e) => setQ(e.target.value)}
                onSearch={(v) => { setQ(v); fetchReport(); }}
                placeholder="Code / name / serial" style={{ width: 240 }} />
            </Space>
          </Space>
        </Card>

        {loadError ? (
          <Alert type="error" showIcon style={{ marginBottom: 10 }}
            message="Could not load Fixed Asset Schedule"
            description={friendlyReportError(loadError)}
            action={<Button size="small" icon={<ReloadOutlined />}
              onClick={fetchReport}>Retry</Button>} />
        ) : null}

        {/* Body */}
        {view === 'grouped' && groupedAvailable ? (
          <>
            {groups.map((g) => (
              <Card key={g.categoryId || '__uncategorised__'}
                size="small"
                title={
                  <div style={{ display: 'flex', justifyContent: 'space-between',
                                alignItems: 'baseline' }}>
                    <span>
                      <Text strong>{g.categoryName}</Text>
                      {g.categoryCode ? (
                        <Text type="secondary" style={{ marginLeft: 6, fontSize: 11.5 }}>
                          {g.categoryCode}
                        </Text>
                      ) : null}
                    </span>
                    <span style={{ fontSize: 12 }}>
                      <Text type="secondary">{g.assetCount} asset(s) · </Text>
                      <Text strong>WDV ₹ {formatRupee(g.totalWdv)}</Text>
                    </span>
                  </div>
                }
                style={{ marginBottom: 10 }}>
                <Table size="small" rowKey="id" columns={columns}
                  dataSource={g.assets} pagination={false} />
              </Card>
            ))}
          </>
        ) : (
          <Card size="small" style={{ marginBottom: 10 }}>
            <Table
              size="small"
              rowKey="id"
              loading={loading}
              columns={columns}
              dataSource={rows}
              pagination={{ pageSize: 50, size: 'small' }}
              locale={{ emptyText:
                <Empty description={loading ? 'Loading…' : 'No fixed assets match the current filters.'} /> }}
            />
          </Card>
        )}

        <div style={{ marginTop: 8, color: '#94a3b8', fontSize: 11.5 }}>
          Basis: {meta.basis || 'fixed_assets_register'} ·
          {meta.tablePresent === false
            ? ' Fixed Assets module not provisioned in this tenant.'
            : ` ${summary.totalCount} asset(s) in scope.`}
        </div>
      </div>
    </ErpMasterShell>
  );
}
